import { useCallback, useState } from 'react';
import { PageHeader } from '../../../components/layout/Header';
import { Button } from '../../../components/ui';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import Spinner from '../../../components/ui/Spinner';
import type { AdminWhatsappTemplateRow } from '../../../domain/superadmin/types';
import { canSendTestWhatsapp } from '../../../domain/superadmin/whatsappSendGuards';
import { useAdminWhatsappCommsSettings } from '../hooks/useAdminWhatsappCommsSettings';
import { useAdminWhatsappSendLog } from '../hooks/useAdminWhatsappSendLog';
import { useAdminWhatsappSystemInstance } from '../hooks/useAdminWhatsappSystemInstance';
import { useAdminWhatsappTemplates } from '../hooks/useAdminWhatsappTemplates';
import { WhatsappConnectQrModal } from './WhatsappConnectQrModal';
import { WhatsappSendLogSection } from './WhatsappSendLogSection';
import { WhatsappSystemInstancePanel } from './WhatsappSystemInstancePanel';
import { WhatsappTemplatesGrid } from './WhatsappTemplatesGrid';
import { WhatsappTemplatePreviewModal } from './WhatsappTemplatePreviewModal';
import { WhatsappTemplateTestSendModal } from './WhatsappTemplateTestSendModal';
import { notify } from '../../../utils/toast';

const EMPTY_INSTANCE = {
    configured: false,
    instanceName: null,
    connected: false,
    phone: null,
    profileName: null,
    qrBase64: null,
} as const;

export function AdminCommsWhatsappPage() {
    const {
        instance,
        isLoading: instLoading,
        error: instError,
        connect,
        reconnect,
        disconnect,
        refetch: refetchInst,
        pollStatus,
        isMutating: instMutating,
    } = useAdminWhatsappSystemInstance();
    const {
        settings,
        setCommsEnabled,
        error: settingsError,
        refetch: refetchSettings,
    } = useAdminWhatsappCommsSettings();
    const { items, isLoading, error, refetch, setDeliveryActive } = useAdminWhatsappTemplates();
    const log = useAdminWhatsappSendLog();

    const [qrOpen, setQrOpen] = useState(false);
    const [initialQr, setInitialQr] = useState<string | null>(null);
    const [previewTemplate, setPreviewTemplate] = useState<AdminWhatsappTemplateRow | null>(null);
    const [testTemplate, setTestTemplate] = useState<AdminWhatsappTemplateRow | null>(null);

    const canSend = canSendTestWhatsapp({
        commsEnabled: Boolean(settings?.commsEnabled),
        systemConfigured: Boolean(instance?.configured),
        connected: Boolean(instance?.connected),
    });

    const adminMissing = instError === 'UAZAPI admin não configurado';

    const handleConnect = async () => {
        const result = await connect();
        if (!result || result.connected) return;
        setInitialQr(result.qrBase64);
        setQrOpen(true);
    };

    const handleReconnect = async () => {
        const result = await reconnect();
        if (!result || result.connected) return;
        setInitialQr(result.qrBase64);
        setQrOpen(true);
    };

    const handleDisconnect = async () => {
        const ok = await notify.confirm('Desconectar a instância de sistema?', {
            description: 'Será preciso conectar novamente (ou outro número) para enviar templates.',
            confirmText: 'Sim, desconectar',
            cancelText: 'Cancelar',
        });
        if (!ok) return;
        await disconnect();
    };

    const handleQrClose = useCallback(() => {
        setQrOpen(false);
    }, []);

    const handlePoll = useCallback(async () => {
        const fresh = await pollStatus();
        return fresh ?? EMPTY_INSTANCE;
    }, [pollStatus]);

    const handleQrConnected = useCallback(() => {
        setQrOpen(false);
        setInitialQr(null);
        void refetchInst();
    }, [refetchInst]);

    const handleSendTestFromPreview = (template: AdminWhatsappTemplateRow) => {
        setPreviewTemplate(null);
        setTestTemplate(template);
    };

    const handleTestClose = () => {
        setTestTemplate(null);
        void log.refetch();
    };

    return (
        <div className="flex flex-col">
            <PageHeader
                title="Templates de WhatsApp"
                subtitle="Mensagens transacionais da plataforma — mesmos eventos do e-mail, quando comunicação e template estão ativos."
                subtitleClassName="text-balance"
                hideBackButton
            />

            {instError && (
                <Alert variant="destructive" className="mb-s4">
                    <AlertTitle>Erro ao carregar instância</AlertTitle>
                    <AlertDescription className="flex flex-col gap-3">
                        <p>{instError}</p>
                        {adminMissing && (
                            <p>Configure UAZAPI_URL e UAZAPI_TOKEN nos Edge Secrets.</p>
                        )}
                        <Button variant="outline" size="sm" onClick={() => void refetchInst()}>
                            Tentar novamente
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            {settingsError && (
                <Alert variant="destructive" className="mb-s4">
                    <AlertTitle>Erro ao carregar configurações</AlertTitle>
                    <AlertDescription className="flex flex-col gap-3">
                        <p>{settingsError}</p>
                        <Button variant="outline" size="sm" onClick={() => void refetchSettings()}>
                            Tentar novamente
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            <WhatsappSystemInstancePanel
                instance={instance}
                isLoading={instLoading}
                instanceError={instError}
                settingsError={settingsError}
                commsEnabled={Boolean(settings?.commsEnabled)}
                onCommsChange={(enabled) => void setCommsEnabled(enabled)}
                onConnect={() => void handleConnect()}
                onReconnect={() => void handleReconnect()}
                onDisconnect={() => void handleDisconnect()}
                isMutating={instMutating}
            />

            {isLoading && (
                <div className="flex items-center justify-center py-24">
                    <Spinner color="primary" size="lg" />
                </div>
            )}

            {!isLoading && error && (
                <Alert variant="destructive">
                    <AlertTitle>Erro ao carregar</AlertTitle>
                    <AlertDescription className="flex flex-col gap-3">
                        <p>{error}</p>
                        <Button variant="outline" size="sm" onClick={() => void refetch()}>
                            Tentar novamente
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            {!isLoading && !error && (
                <WhatsappTemplatesGrid
                    items={items}
                    onPreview={setPreviewTemplate}
                    onTest={setTestTemplate}
                    onDeliveryChange={(template, active) =>
                        void setDeliveryActive(template.key, active)
                    }
                    testDisabled={!canSend}
                />
            )}

            <WhatsappSendLogSection
                items={log.items}
                total={log.total}
                limit={log.limit}
                offset={log.offset}
                isLoading={log.isLoading}
                error={log.error}
                hasNext={log.hasNext}
                hasPrev={log.hasPrev}
                onNext={log.nextPage}
                onPrev={log.prevPage}
                onRetry={() => void log.refetch()}
            />

            <WhatsappConnectQrModal
                isOpen={qrOpen}
                onClose={handleQrClose}
                initialQr={initialQr}
                onPoll={handlePoll}
                onConnected={handleQrConnected}
            />

            <WhatsappTemplatePreviewModal
                template={previewTemplate}
                isOpen={previewTemplate !== null}
                onClose={() => setPreviewTemplate(null)}
                onSendTest={handleSendTestFromPreview}
            />

            <WhatsappTemplateTestSendModal
                template={testTemplate}
                isOpen={testTemplate !== null}
                onClose={handleTestClose}
                disabled={!canSend}
            />
        </div>
    );
}
